/**
 * MASTER — STEP_BY_STEP / «Шаг за шагом».
 *
 * A clear practical sequence of actions. Unlike PROCESS_STORY (which explains
 * why the transformation happens), this master explains WHAT TO DO — step
 * by step. Reusable across every Digital Editions surface. Nothing here is
 * Almond, product, material or route specific.
 *
 * Storage:
 *   `block.content.step_by_step` (schema exposes it as
 *   `Optional[Dict[str, Any]]`). Legacy top-level keys of `block.content`
 *   are preserved verbatim on save.
 *
 * Shared primitives reused from HeroMaster / CropModal — no second media
 * workflow is created here. Per-step image is optional.
 */
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  ArrowUp, ArrowDown, Trash2, Plus, ListOrdered,
} from "lucide-react";
import CropModal from "@/components/editorial/CropModal";
import { ImageSlot, MediaLibraryPicker } from "@/components/editorial/HeroMaster";
import { resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle } from "@/lib/editorialReaderLayout.mjs";

const genId = (p = "id") =>
  `${p}${Date.now().toString(36)}${Math.floor(Math.random() * 1e4)}`;

const emptyImage = () => ({
  media_id: null, url: null, alt: "", focal_x: 0.5, focal_y: 0.5, zoom: 1,
});

function readSteps(rawContent) {
  const c = rawContent || {};
  const s = (c.step_by_step && typeof c.step_by_step === "object")
    ? c.step_by_step : {};

  const steps = Array.isArray(s.steps)
    ? s.steps.map((st) => ({
        id:          st.id          || genId("st"),
        title:       st.title       || "",
        instruction: st.instruction || "",
        parameter:   st.parameter   || "",
        image: {
          media_id: st.image?.media_id ?? null,
          url:      st.image?.url      ?? null,
          alt:      st.image?.alt      ?? "",
          focal_x:  st.image?.focal_x  ?? 0.5,
          focal_y:  st.image?.focal_y  ?? 0.5,
          zoom:     st.image?.zoom     ?? 1,
        },
      }))
    : [];

  return {
    eyebrow:      s.eyebrow      ?? "",
    title:        s.title        ?? c.headline ?? "",
    introduction: s.introduction ?? c.lead     ?? "",
    conclusion:   s.conclusion   ?? "",
    steps,
  };
}

// -------------------- Editor --------------------

export function StepByStepEditor({ block, onPatch }) {
  const s = useMemo(() => readSteps(block.content), [block.content]);
  const patch = (delta) => onPatch({
    content: { ...(block.content || {}), step_by_step: { ...s, ...delta } },
  });

  const [libraryFor, setLibraryFor] = useState(null);
  const [cropFor, setCropFor] = useState(null);

  const setSteps = (steps) => patch({ steps });
  const addStep = () => setSteps([
    ...s.steps,
    { id: genId("st"), title: "", instruction: "", parameter: "", image: emptyImage() },
  ]);
  const removeStep = (i) => {
    const next = [...s.steps]; next.splice(i, 1); setSteps(next);
  };
  const moveStep = (i, dir) => {
    const next = [...s.steps]; const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setSteps(next);
  };
  const patchStep = (i, delta) => {
    const next = [...s.steps]; next[i] = { ...next[i], ...delta };
    setSteps(next);
  };
  const patchStepImage = (i, delta) => {
    const step = s.steps[i];
    patchStep(i, { image: { ...step.image, ...delta } });
  };

  return (
    <div className="grid grid-cols-1 gap-4" data-testid="stepbystep-editor">
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Шаг за шагом · заголовок</div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Метка раздела · необязательно</label>
          <Input value={s.eyebrow} onChange={(e) => patch({ eyebrow: e.target.value })}
                 placeholder="ШАГ ЗА ШАГОМ"
                 data-testid="stepbystep-field-eyebrow" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Заголовок</label>
          <Input value={s.title} onChange={(e) => patch({ title: e.target.value })}
                 className="font-serif text-lg"
                 data-testid="stepbystep-field-title" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Введение · необязательно</label>
          <textarea rows={3} value={s.introduction}
                    onChange={(e) => patch({ introduction: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm italic font-serif leading-relaxed"
                    data-testid="stepbystep-field-introduction" />
        </div>
      </div>

      <div className="rounded-2xl bg-white border hairline p-4 space-y-3">
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] uppercase tracking-widest opacity-70">Шаги</div>
          <button type="button" onClick={addStep}
            className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
            data-testid="stepbystep-add-step">
            <Plus className="w-3.5 h-3.5" /> Добавить шаг
          </button>
        </div>
        {s.steps.length === 0 ? (
          <div className="rounded-lg bg-cream/60 border border-dashed border-espresso/25 p-4 text-center">
            <div className="text-[12px] opacity-70">Ни одного шага не добавлено.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {s.steps.map((st, i) => (
              <div key={st.id} className="rounded-lg border hairline bg-cream/40 p-3 space-y-3"
                   data-testid={`stepbystep-item-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="text-[10px] uppercase tracking-widest opacity-60 shrink-0 font-mono">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <Input value={st.title}
                         onChange={(e) => patchStep(i, { title: e.target.value })}
                         placeholder="Заголовок шага"
                         className="font-serif"
                         data-testid={`stepbystep-title-${i}`} />
                  <div className="flex items-center gap-1 shrink-0">
                    <button type="button" onClick={() => moveStep(i, -1)} disabled={i === 0}
                      className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                      title="Выше" data-testid={`stepbystep-up-${i}`}>
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => moveStep(i, +1)} disabled={i === s.steps.length - 1}
                      className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                      title="Ниже" data-testid={`stepbystep-down-${i}`}>
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => removeStep(i)}
                      className="p-1 opacity-60 hover:opacity-100 text-red-600"
                      title="Удалить" data-testid={`stepbystep-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_240px] gap-3">
                  <div className="space-y-2">
                    <div>
                      <label className="block text-[11px] opacity-60 mb-1">Инструкция</label>
                      <textarea rows={4} value={st.instruction}
                                onChange={(e) => patchStep(i, { instruction: e.target.value })}
                                className="w-full border rounded px-2 py-1.5 text-sm font-serif leading-relaxed"
                                placeholder="Что именно сделать на этом шаге"
                                data-testid={`stepbystep-instruction-${i}`} />
                    </div>
                    <Input value={st.parameter}
                           onChange={(e) => patchStep(i, { parameter: e.target.value })}
                           placeholder="Ключевой параметр · необязательно"
                           data-testid={`stepbystep-parameter-${i}`} />
                  </div>
                  <div>
                    <label className="block text-[11px] opacity-60 mb-1">Фото шага · необязательно</label>
                    <ImageSlot
                      label=""
                      url={st.image.url}
                      focalX={st.image.focal_x} focalY={st.image.focal_y} zoom={st.image.zoom}
                      onFile={(x) => patchStepImage(i, { media_id: x.id, url: x.file_url })}
                      onClear={() => patchStepImage(i, { media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
                      onOpenLibrary={() => setLibraryFor(i)}
                      onOpenCrop={() => setCropFor(i)}
                      testid={`stepbystep-image-${i}`} />
                    <div className="mt-2">
                      <Input placeholder="Описание изображения · необязательно"
                             value={st.image.alt}
                             onChange={(e) => patchStepImage(i, { alt: e.target.value })}
                             data-testid={`stepbystep-alt-${i}`} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-white border hairline p-4 space-y-2">
        <label className="block text-[11px] opacity-60 mb-1">Заключение · необязательно</label>
        <textarea rows={3} value={s.conclusion}
                  onChange={(e) => patch({ conclusion: e.target.value })}
                  className="w-full border rounded px-2 py-1.5 text-sm font-serif leading-relaxed"
                  placeholder="Итоговая мысль, связывающая шаги"
                  data-testid="stepbystep-field-conclusion" />
      </div>

      <MediaLibraryPicker
        open={libraryFor != null}
        onClose={() => setLibraryFor(null)}
        onPick={(x) => {
          if (libraryFor == null) return;
          patchStepImage(libraryFor, { media_id: x.id, url: x.file_url || x.url });
          setLibraryFor(null);
        }} />
      <CropModal
        open={cropFor != null}
        url={cropFor != null ? s.steps[cropFor]?.image?.url : null}
        focalX={cropFor != null ? s.steps[cropFor]?.image?.focal_x ?? 0.5 : 0.5}
        focalY={cropFor != null ? s.steps[cropFor]?.image?.focal_y ?? 0.5 : 0.5}
        zoom={cropFor != null ? s.steps[cropFor]?.image?.zoom || 1 : 1}
        onClose={() => setCropFor(null)}
        onSave={({ focal_x, focal_y, zoom }) => {
          if (cropFor == null) return;
          patchStepImage(cropFor, { focal_x, focal_y, zoom });
          setCropFor(null);
        }} />
    </div>
  );
}

// -------------------- Render --------------------

function StepImage({ img, mode = "desktop" }) {
  if (!img?.url) return null;
  const maxH = mode === "mobile" ? 420 : 680;
  return (
    <div className="w-full aspect-[4/5] overflow-hidden bg-neutral-100"
         style={{ maxHeight: maxH }}
         data-testid="stepbystep-image">
      <img src={resolveMediaUrl(img.url)} alt={img.alt || ""}
           className="h-full w-full"
           style={editorialImageStyle(img)} />
    </div>
  );
}

function StepBlock({ step, index, mode }) {
  const isMobile = mode === "mobile";
  const num = String(index + 1).padStart(2, "0");
  const hasImage = !!(step.image && step.image.url);

  // Matches PROCESS_STORY scale: 28px number, phase-title 19–22px.
  const NumberBadge = (
    <div className="font-serif tabular-nums leading-none opacity-70"
         style={{
           color: "#A67B4A",
           fontSize: isMobile ? "26px" : "28px",
           letterSpacing: "-0.02em",
         }}>{num}</div>
  );

  const TextBlock = (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-6 items-baseline">
      {NumberBadge}
      <div className="min-w-0">
        {step.title && (
          <h4 className="font-serif leading-[1.25]"
              style={{
                fontSize: isMobile ? "clamp(17px, 4.6vw, 19px)" : "clamp(19px, 1.5vw, 22px)",
                color: "#2A1D14",
              }}>{step.title}</h4>
        )}
        {step.instruction && (
          <p className="mt-2 font-serif text-[15px] leading-[1.7] whitespace-pre-wrap max-w-[62ch]"
             style={{ color: "#2A1D14" }}>{step.instruction}</p>
        )}
        {step.parameter && (
          <div className="mt-3 inline-flex items-baseline gap-2 py-1 px-3 rounded-full bg-cream/70 border border-espresso/10">
            <span className="text-[9px] tracking-[0.24em] uppercase opacity-70"
                  style={{ color: "#5B4326" }}>Параметр</span>
            <span className="font-serif text-[13px]" style={{ color: "#2A1D14" }}>{step.parameter}</span>
          </div>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="space-y-3" data-testid={`stepbystep-block-${step.id}`}>
        {TextBlock}
        {hasImage && <StepImage img={step.image} mode="mobile" />}
      </div>
    );
  }

  if (!hasImage) {
    return <div data-testid={`stepbystep-block-${step.id}`}>{TextBlock}</div>;
  }
  const imageOnLeft = index % 2 === 0;
  const ImageCol = <StepImage img={step.image} mode="desktop" />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-8 lg:gap-12 items-start"
         data-testid={`stepbystep-block-${step.id}`}>
      {imageOnLeft ? <>{ImageCol}<div>{TextBlock}</div></> : <><div>{TextBlock}</div>{ImageCol}</>}
    </div>
  );
}

export function StepByStepRender({ block, mode = "desktop" }) {
  const s = useMemo(() => readSteps(block.content), [block.content]);
  const isMobile = mode === "mobile";

  const steps = (s.steps || []).filter(
    (st) => st.title || st.instruction || st.parameter || (st.image && st.image.url));

  const Eyebrow = s.eyebrow ? (
    <div className="inline-flex items-center gap-2 mb-4 text-[10px] tracking-[0.28em] uppercase"
         style={{ color: "#8A6A46" }}
         data-testid="stepbystep-eyebrow">
      <ListOrdered className="w-3.5 h-3.5" />
      {s.eyebrow}
    </div>
  ) : null;

  const Title = s.title ? (
    <h2 className="font-serif leading-[1.08]"
        style={{
          fontSize: isMobile ? "clamp(23px, 5.4vw, 28px)" : "clamp(26px, 2.6vw, 36px)",
          color: "#2A1D14",
          letterSpacing: "-0.005em",
        }}
        data-testid="stepbystep-title">{s.title}</h2>
  ) : null;

  const Intro = s.introduction ? (
    <p className="mt-3 font-serif italic text-[15px] leading-[1.6] max-w-[62ch]"
       style={{ color: "#5B4326" }}
       data-testid="stepbystep-intro">{s.introduction}</p>
  ) : null;

  const Conclusion = s.conclusion ? (
    <div className={isMobile ? "mt-6 pl-4 border-l-2" : "mt-8 pl-4 border-l-2"}
         style={{ borderColor: "#A67B4A" }}
         data-testid="stepbystep-conclusion">
      <p className="font-serif italic text-[15px] leading-[1.6] max-w-[62ch]"
         style={{ color: "#3E2E20" }}>{s.conclusion}</p>
    </div>
  ) : null;

  const Steps = steps.length ? (
    <div className={isMobile ? "mt-6 space-y-8" : "mt-8 space-y-10"}
         data-testid="stepbystep-list">
      {steps.map((st, i) => <StepBlock key={st.id} step={st} index={i} mode={mode} />)}
    </div>
  ) : null;

  // Whether any step has an image — controls desktop column width.
  const anyImage = steps.some((st) => st.image && st.image.url);

  return (
    <section className="w-full overflow-hidden"
             style={{ background: "#EFE6D5" }}
             data-testid={`stepbystep-render-${block.id}-${mode}`}>
      <div className={`mx-auto px-6 sm:px-10 py-10 sm:py-14 ${isMobile ? "" : (anyImage ? "max-w-[1200px]" : "max-w-[760px]")}`}>
        {Eyebrow}
        {Title}
        {Intro}
        {Steps}
        {Conclusion}
      </div>
    </section>
  );
}
